import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { acceleratorsData } from '../data/acceleratorsData';
import BlogSignUp from './BlogSignUp';
import BlogSignIn from './BlogSignIn';
import { X, Filter, Search, MapPin, Users, DollarSign, Calendar, Award, Share2 } from 'lucide-react';

  const Top100Investors = () => {
  const { currentUser } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [selectedAccelerator, setSelectedAccelerator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Use the full dataset from the data file
  const accelerators = acceleratorsData;

  // Extract countries from locations and get unique countries for filters
  const extractCountry = (location) => {
    // Common patterns for extracting country from location strings
    const countryPatterns = [
      /,\s*([^,]+)$/, // Matches "City, State, Country" -> "Country"
      /\s+([A-Z]{2})$/, // Matches "City, State, USA" -> "USA"
      /\s+([A-Z]{3})$/, // Matches "City, State, UAE" -> "UAE"
    ];
    
    for (const pattern of countryPatterns) {
      const match = location.match(pattern);
      if (match) return match[1];
    }
    
    // Fallback: return the last part after the last comma
    const parts = location.split(',').map(part => part.trim());
    return parts[parts.length - 1] || location;
  };

  // Filter accelerators based on search and filters
  const filteredAccelerators = useMemo(() => {
    return accelerators.filter(accelerator => {
      const matchesSearch = accelerator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           accelerator.leader.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCountry = !countryFilter || extractCountry(accelerator.location).toLowerCase().includes(countryFilter.toLowerCase());
      const matchesType = !companyTypeFilter || accelerator.companyTypes.toLowerCase().includes(companyTypeFilter.toLowerCase());
      
      return matchesSearch && matchesCountry && matchesType;
    });
  }, [accelerators, searchTerm, countryFilter, companyTypeFilter]);

  const displayedAccelerators = showAll ? filteredAccelerators : filteredAccelerators.slice(0, 15);

  const countries = [...new Set(accelerators.map(a => extractCountry(a.location)))].sort();
  const companyTypes = [...new Set(accelerators.map(a => a.companyTypes))].sort();

  return (
    <div className="min-h-screen bg-slate-900 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <h1 className="text-5xl font-light text-white">
              Top 100 Accelerators & Incubators 2025
            </h1>
            <button
              onClick={async () => {
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: 'Top 100 Accelerators & Incubators 2025',
                      text: 'Discover the latest 2025 rankings of the most successful startup accelerators and incubators worldwide.',
                      url: window.location.href
                    });
                  } catch (error) {
                    // User cancelled the share or other error occurred
                    // Silently handle the error - no need to show anything to user
                    console.log('Share was cancelled or failed:', error);
                  }
                } else {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    // You could add a toast notification here for success
                  } catch (error) {
                    console.log('Failed to copy to clipboard:', error);
                  }
                }
              }}
              className="p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-slate-300 hover:bg-slate-800/70 hover:text-white transition-all duration-200 hover:scale-105"
              title="Share this page"
            >
              <Share2 className="w-6 h-6 font-bold" strokeWidth={2.5} />
            </button>
          </div>
          <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed">
            Discover the latest 2025 rankings of the most successful startup accelerators and incubators worldwide. 
            Get exclusive insights on current funding terms, acceptance rates, and success stories.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search accelerators or leaders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-xl text-slate-300 hover:bg-slate-800/70 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <span className="text-slate-400 text-sm">
              {filteredAccelerators.length} results
            </span>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
                  <select
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">All Countries</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Company Type</label>
                  <select
                    value={companyTypeFilter}
                    onChange={(e) => setCompanyTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">All Types</option>
                    {companyTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700/50">
              <thead className="bg-slate-800/30">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Accelerator Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Companies
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Program Leader
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Investment Terms
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Program Fee
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Program Length
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Cohorts/Year
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Company Types
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Acceptance Rate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Format
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Top Alumni
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Alumni Exits
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-slate-800/20 divide-y divide-slate-700/30">
                {displayedAccelerators.map((accelerator) => (
                  <tr 
                    key={accelerator.rank} 
                    className="hover:bg-slate-800/40 transition-colors duration-200 cursor-pointer"
                    onClick={() => setSelectedAccelerator(accelerator)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      #{accelerator.rank}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{accelerator.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.companies}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.leader}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.investment}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.fee}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.batches}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.companyTypes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.acceptance}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.format}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.topAlumni}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {accelerator.alumniExits}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        {accelerator.rating}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {displayedAccelerators.map((accelerator) => (
            <div
              key={accelerator.rank}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 cursor-pointer hover:bg-slate-800/70 transition-colors"
              onClick={() => setSelectedAccelerator(accelerator)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-400">#{accelerator.rank}</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {accelerator.rating}
                    </span>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-1">{accelerator.name}</h3>
                  <p className="text-sm text-slate-400">{accelerator.leader}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span>{accelerator.companies} companies</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="truncate">{accelerator.location}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="truncate">{accelerator.investment}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>{accelerator.length}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sign up/sign in prompt */}
        {!showAll && !currentUser && (
          <div className="mt-12">
            {showSignUp ? (
              <BlogSignUp 
                onSignUpSuccess={() => {
                  setShowSignUp(false);
                  setShowAll(true);
                }}
              />
            ) : showSignIn ? (
              <BlogSignIn 
                onSignInSuccess={() => {
                  setShowSignIn(false);
                  setShowAll(true);
                }}
              />
            ) : (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-8 text-center">
                <h3 className="text-2xl font-light text-white mb-4">
                  Want to see the complete list?
                </h3>
                <p className="text-lg mb-8 text-slate-300 leading-relaxed">
                  Get access to all 100 top accelerators and incubators with detailed information, 
                  including success stories, alumni companies, and application tips.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setShowSignUp(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Sign Up Free
                  </button>
                  <button
                    onClick={() => setShowSignIn(true)}
                    className="bg-transparent border border-slate-600 text-slate-300 px-8 py-3 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all duration-200"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show full list for authenticated users */}
        {currentUser && !showAll && (
          <div className="mt-12 bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
            <h3 className="text-lg font-light text-white mb-3">
              🎉 Full Access Granted!
            </h3>
            <p className="text-slate-300 mb-6 leading-relaxed">
              You now have access to the complete list of 100 top accelerators and incubators. 
              Use this information to find the best funding opportunities for your startup.
            </p>
            <button
              onClick={() => setShowAll(true)}
              className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:from-green-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              View Complete List
            </button>
          </div>
        )}

        {/* Additional info for signed-in users */}
        {currentUser && showAll && (
          <div className="mt-12 bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
            <h3 className="text-lg font-light text-white mb-3">
              🎉 Full Access Granted!
            </h3>
            <p className="text-slate-300 leading-relaxed">
              You now have access to the complete list of 100 top accelerators and incubators. 
              Use this information to find the best funding opportunities for your startup.
            </p>
          </div>
        )}

        {/* Accelerator Detail Modal */}
        {selectedAccelerator && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800/95 backdrop-blur-sm rounded-2xl border border-slate-700/50 max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-slate-400">#{selectedAccelerator.rank}</span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {selectedAccelerator.rating}
                    </span>
                  </div>
                  <h2 className="text-2xl font-light text-white">{selectedAccelerator.name}</h2>
                </div>
                <button
                  onClick={() => setSelectedAccelerator(null)}
                  className="p-2 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Program Leader</label>
                        <p className="text-white">{selectedAccelerator.leader}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Location</label>
                        <p className="text-white">{selectedAccelerator.location}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Company Types</label>
                        <p className="text-white">{selectedAccelerator.companyTypes}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Format</label>
                        <p className="text-white">{selectedAccelerator.format}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Companies</label>
                        <p className="text-white">{selectedAccelerator.companies}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Acceptance Rate</label>
                        <p className="text-white">{selectedAccelerator.acceptance}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Program Length</label>
                        <p className="text-white">{selectedAccelerator.length}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Cohorts/Year</label>
                        <p className="text-white">{selectedAccelerator.batches}</p>
                      </div>
                    </div>
                  </div>

                  {/* Investment & Fees */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white border-b border-slate-700/50 pb-2">Investment & Fees</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Investment Terms</label>
                        <p className="text-white">{selectedAccelerator.investment}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Program Fee</label>
                        <p className="text-white">{selectedAccelerator.fee}</p>
                      </div>
                    </div>
                  </div>

                  {/* Alumni & Success */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white border-b border-slate-700/50 pb-2">Alumni & Success</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Top Alumni</label>
                        <p className="text-white">{selectedAccelerator.topAlumni}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1 block">Alumni Exits</label>
                        <p className="text-white">{selectedAccelerator.alumniExits}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Top100Investors; 